import {trackServiceLevelOutcome} from '../../service-level/analytics'
import {useEffect, useMemo, useState} from 'react'
import {
  Building2,
  CheckCircle2,
  ChevronRight,
  Database,
  Gauge,
  Layers3,
  Save,
  Search,
  ShieldCheck,
  UserRound,
  UsersRound,
  X,
} from 'lucide-react'
import Select from 'react-select'

import * as API from '../../../api/index'
import {ServiceLevelCatalog, ServiceLevelScope} from '../../../api/index'

import {
  NO_SERVICE_LEVEL_PERMISSIONS,
  SERVICE_LEVEL_PERMISSIONS,
} from '../../service-level/permissions'

const FEATURE_NAME = 'service-level'

const NIVEIS = [
  {
    value: 1,
    label: 'N1 · Somente números',
    desc: 'KPIs consolidados, sem listas de sellers, indústrias ou pedidos.',
  },
  {
    value: 2,
    label: 'N2 · Recortes',
    desc: 'Inclui fechamento por seller, indústria e unidade de negócio.',
  },
  {
    value: 3,
    label: 'N3 · Detalhe completo',
    desc: 'Inclui pedidos, exportação e visualização item a item.',
  },
]

type Opcao = {value: number; label: string}

function initials(name = '') {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

export const ServiceLevelAccess = () => {
  const [carregando, setCarregando] = useState(true)
  const [carregandoEscopo, setCarregandoEscopo] = useState(false)
  const [erroEscopo, setErroEscopo] = useState(false)
  const [tentativaEscopo, setTentativaEscopo] = useState(0)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [busca, setBusca] = useState('')
  const [filtroAcesso, setFiltroAcesso] = useState('all')
  const [usuarios, setUsuarios] = useState<any[]>([])
  const [idFeature, setIdFeature] = useState<number | null>(null)
  const [comAcesso, setComAcesso] = useState<Set<number>>(new Set())
  const [catalogo, setCatalogo] = useState<ServiceLevelCatalog | null>(null)
  const [usuarioSel, setUsuarioSel] = useState<any>(null)
  const [escopo, setEscopo] = useState<ServiceLevelScope>({
    bus: null,
    sellers: null,
    detail_level: 3,
    permissions: {...NO_SERVICE_LEVEL_PERMISSIONS},
  })

  useEffect(() => {
    ;(async () => {
      try {
        const res = await API.getUserFeatures()
        const data = res.data
        const feature = (data.features || []).find((item: any) => item.name === FEATURE_NAME)
        if (!feature) {
          setErro(
            'A funcionalidade “Nível de Serviço” não está habilitada para esta empresa. Peça a um Super Admin para liberá-la em Empresas → Funcionalidades.'
          )
          return
        }

        setIdFeature(feature.id_feature)
        setUsuarios(data.users || [])
        setComAcesso(
          new Set(
            (data.permissions || [])
              .filter((permission: any) => permission.id_feature === feature.id_feature)
              .map((permission: any) => permission.id_user)
          )
        )
        setCatalogo(await API.getServiceLevelCatalog())
      } catch {
        setErro('Não foi possível carregar os dados do Nível de Serviço.')
      } finally {
        setCarregando(false)
      }
    })()
  }, [])

  useEffect(() => {
    if (!usuarioSel) return
    let isCurrent = true
    setAviso(null)
    setCarregandoEscopo(true)
    setErroEscopo(false)

    API.getServiceLevelScope(usuarioSel.guid)
      .then((scope) => {
        if (isCurrent) setEscopo(scope)
      })
      .catch(() => {
        if (isCurrent) setErroEscopo(true)
      })
      .finally(() => {
        if (isCurrent) setCarregandoEscopo(false)
      })

    return () => {
      isCurrent = false
    }
  }, [usuarioSel, tentativaEscopo])

  const usuariosFiltrados = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase('pt-BR')
    return usuarios.filter((usuario) => {
      const possuiAcesso = comAcesso.has(usuario.id_user)
      const correspondeBusca =
        !termo ||
        `${usuario.name || ''} ${usuario.email || ''}`.toLocaleLowerCase('pt-BR').includes(termo)
      const correspondeAcesso =
        filtroAcesso === 'all' || (filtroAcesso === 'enabled' ? possuiAcesso : !possuiAcesso)
      return correspondeBusca && correspondeAcesso
    })
  }, [busca, comAcesso, filtroAcesso, usuarios])

  const opcoesBus: Opcao[] = useMemo(() => {
    const bus = catalogo?.bus || []
    const empresa = escopo.empresa
    const idsIndustria = empresa?.tipo === 'INDUSTRIA' ? empresa.itens.map((item) => item.id) : null
    const filtradas = idsIndustria?.length
      ? bus.filter((item) => idsIndustria.includes(item.industria_id))
      : bus

    return filtradas.map((item) => {
      const industria = catalogo?.industrias.find((ind) => ind.id === item.industria_id)
      return {value: item.id, label: industria ? `${industria.nome} · ${item.nome}` : item.nome}
    })
  }, [catalogo, escopo.empresa])

  const opcoesSellers: Opcao[] = useMemo(() => {
    const todos = catalogo?.sellers || []
    const empresa = escopo.empresa
    const permitidos =
      empresa?.tipo === 'DISTRIBUIDOR' ? empresa.itens.map((item) => item.id) : null
    const lista = permitidos?.length
      ? todos.filter((seller) => permitidos.includes(seller.id))
      : todos
    return lista.map((seller) => ({
      value: seller.id,
      label: `${seller.nome} (${seller.uf || '—'})`,
    }))
  }, [catalogo, escopo.empresa])

  const temAcesso = usuarioSel ? comAcesso.has(usuarioSel.id_user) : false

  const alternarAcesso = async () => {
    if (!usuarioSel || !idFeature) return
    const tinhaAcesso = temAcesso
    setSalvando(true)
    setAviso(null)

    try {
      const payload = {guid: usuarioSel.guid, id_feature: idFeature}
      if (tinhaAcesso) await API.removeFeatureUserPermission(payload)
      else await API.addFeatureUserPermission(payload)

      setComAcesso((current) => {
        const next = new Set(current)
        if (tinhaAcesso) next.delete(usuarioSel.id_user)
        else next.add(usuarioSel.id_user)
        return next
      })
      setAviso(tinhaAcesso ? 'Acesso removido com sucesso.' : 'Acesso liberado com sucesso.')
    } catch {
      setAviso('Falha ao alterar o acesso. Tente novamente.')
    } finally {
      setSalvando(false)
    }
  }

  const salvarEscopo = async () => {
    if (!usuarioSel) return
    setSalvando(true)
    setAviso(null)

    try {
      const salvo = await API.putServiceLevelScope(usuarioSel.guid, escopo)
      setEscopo(salvo)
      setAviso('Permissões e escopo salvos com sucesso.')
      trackServiceLevelOutcome('scope', true)
    } catch {
      trackServiceLevelOutcome('scope', false)
      setAviso('Falha ao salvar as permissões e o escopo. Tente novamente.')
    } finally {
      setSalvando(false)
    }
  }

  const valorMulti = (ids: number[] | null, opcoes: Opcao[]) =>
    ids?.length ? opcoes.filter((opcao) => ids.includes(opcao.value)) : []

  if (carregando) {
    return (
      <div className='ua-card ua-loading'>
        <span className='ua-spinner' /> Carregando acessos do Nível de Serviço…
      </div>
    )
  }

  if (erro) {
    return (
      <div className='ua-empty'>
        <div>
          <span className='ua-empty__icon'>
            <Gauge size={22} />
          </span>
          <h3>Nível de Serviço indisponível</h3>
          <p>{erro}</p>
        </div>
      </div>
    )
  }

  return (
    <section className='ua-section'>
      <header className='ua-section__heading'>
        <div className='ua-section__heading-copy'>
          <span className='ua-section__heading-icon'>
            <Gauge size={20} />
          </span>
          <div>
            <h2>Nível de Serviço</h2>
            <p>
              Defina o acesso às telas, as permissões e a profundidade das informações por usuário.
            </p>
          </div>
        </div>
        <span className='ua-badge ua-badge--success'>Funcionalidade habilitada</span>
      </header>

      <div className='ua-stats'>
        <div className='ua-stat'>
          <span className='ua-stat__icon'>
            <UsersRound size={18} />
          </span>
          <span>
            <strong className='ua-stat__value'>{usuarios.length}</strong>
            <small className='ua-stat__label'>Usuários disponíveis</small>
          </span>
        </div>
        <div className='ua-stat'>
          <span className='ua-stat__icon'>
            <CheckCircle2 size={18} />
          </span>
          <span>
            <strong className='ua-stat__value'>{comAcesso.size}</strong>
            <small className='ua-stat__label'>Com acesso ao painel</small>
          </span>
        </div>
        <div className='ua-stat'>
          <span className='ua-stat__icon'>
            <ShieldCheck size={18} />
          </span>
          <span>
            <strong className='ua-stat__value'>
              {Math.max(usuarios.length - comAcesso.size, 0)}
            </strong>
            <small className='ua-stat__label'>Sem acesso ao painel</small>
          </span>
        </div>
        <div className='ua-stat'>
          <span className='ua-stat__icon'>
            <Database size={18} />
          </span>
          <span>
            <strong className='ua-stat__value'>
              {(catalogo?.bus?.length || 0) + (catalogo?.sellers?.length || 0)}
            </strong>
            <small className='ua-stat__label'>Itens no catálogo de escopo</small>
          </span>
        </div>
      </div>

      <div className='ua-service-grid'>
        <aside className='ua-card ua-service-users'>
          <div className='ua-card__header'>
            <div>
              <h3>Escolha um usuário</h3>
              <p>{usuariosFiltrados.length} resultado(s)</p>
            </div>
          </div>
          <div className='ua-service-users__filters'>
            <div className='ua-search'>
              <Search size={15} />
              <input
                value={busca}
                placeholder='Buscar nome ou e-mail'
                aria-label='Buscar usuário'
                onChange={(event) => setBusca(event.target.value)}
              />
              {busca && (
                <button
                  type='button'
                  className='ua-search__clear'
                  aria-label='Limpar busca'
                  onClick={() => setBusca('')}
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className='ua-select'>
              <select
                value={filtroAcesso}
                aria-label='Filtrar por acesso'
                onChange={(event) => setFiltroAcesso(event.target.value)}
              >
                <option value='all'>Todos os acessos</option>
                <option value='enabled'>Com acesso</option>
                <option value='disabled'>Sem acesso</option>
              </select>
            </div>
          </div>
          <div className='ua-service-users__list'>
            {usuariosFiltrados.length ? (
              usuariosFiltrados.map((usuario) => {
                const possuiAcesso = comAcesso.has(usuario.id_user)
                const selecionado = usuarioSel?.id_user === usuario.id_user
                return (
                  <button
                    type='button'
                    key={usuario.id_user}
                    className={`ua-service-user ${selecionado ? 'is-active' : ''}`}
                    onClick={() => setUsuarioSel(usuario)}
                    disabled={salvando}
                  >
                    <span className='ua-avatar'>
                      {usuario.src ? <img src={usuario.src} alt='' /> : initials(usuario.name)}
                    </span>
                    <span className='ua-service-user__copy'>
                      <strong>{usuario.name}</strong>
                      <small>{usuario.email}</small>
                    </span>
                    <span
                      className={`ua-service-user__status ${possuiAcesso ? 'is-enabled' : ''}`}
                      title={possuiAcesso ? 'Com acesso' : 'Sem acesso'}
                    />
                    <ChevronRight size={15} />
                  </button>
                )
              })
            ) : (
              <div className='ua-service-users__empty'>Nenhum usuário corresponde aos filtros.</div>
            )}
          </div>
        </aside>

        <div className='ua-card ua-service-config'>
          {!usuarioSel ? (
            <div className='ua-empty ua-empty--inside ua-service-config__empty'>
              <div>
                <span className='ua-empty__icon'>
                  <UserRound size={22} />
                </span>
                <h3>Selecione um usuário</h3>
                <p>Escolha uma pessoa na lista para visualizar e configurar seu acesso.</p>
              </div>
            </div>
          ) : carregandoEscopo ? (
            <div className='ua-loading'>
              <span className='ua-spinner' /> Carregando escopo de {usuarioSel.name}…
            </div>
          ) : erroEscopo ? (
            <div className='ua-service-config__body'>
              <p className='ua-feedback ua-feedback--danger' role='alert'>
                Não foi possível carregar as permissões e o escopo deste usuário.
              </p>
              <button
                type='button'
                className='ua-primary-button'
                onClick={() => setTentativaEscopo((value) => value + 1)}
              >
                Tentar novamente
              </button>
            </div>
          ) : (
            <>
              <div className='ua-service-config__header'>
                <div className='ua-user-cell'>
                  <span className='ua-avatar'>
                    {usuarioSel.src ? (
                      <img src={usuarioSel.src} alt='' />
                    ) : (
                      initials(usuarioSel.name)
                    )}
                  </span>
                  <span className='ua-user-cell__copy'>
                    <strong>{usuarioSel.name}</strong>
                    <small>{usuarioSel.email}</small>
                  </span>
                </div>
                <span
                  className={`ua-badge ${temAcesso ? 'ua-badge--success' : 'ua-badge--danger'}`}
                >
                  {temAcesso ? 'Acesso liberado' : 'Sem acesso'}
                </span>
              </div>

              <div className='ua-service-config__body'>
                <div className={`ua-access-control ${temAcesso ? 'is-enabled' : ''}`}>
                  <div>
                    <span className='ua-access-control__icon'>
                      <ShieldCheck size={19} />
                    </span>
                    <span>
                      <strong>Acesso ao painel</strong>
                      <small>
                        {temAcesso
                          ? 'A pessoa visualiza o Nível de Serviço no menu e na página inicial.'
                          : 'Libere o acesso antes de configurar o escopo de dados.'}
                      </small>
                    </span>
                  </div>
                  <button
                    type='button'
                    className={temAcesso ? 'ua-danger-button' : 'ua-primary-button'}
                    onClick={alternarAcesso}
                    disabled={salvando}
                  >
                    {temAcesso ? 'Remover acesso' : 'Liberar acesso'}
                  </button>
                </div>

                {aviso && (
                  <div
                    className={`ua-feedback ${
                      aviso.startsWith('Falha') ? 'ua-feedback--danger' : ''
                    }`}
                  >
                    {aviso}
                  </div>
                )}

                <div className={`ua-service-form ${temAcesso ? '' : 'is-disabled'}`}>
                  <fieldset className='ua-service-permissions' disabled={!temAcesso || salvando}>
                    <legend>Permissões de administração</legend>
                    <p>
                      Libere cada tela de forma independente. Super Admins mantêm acesso completo.
                    </p>
                    <div className='ua-service-permissions__options'>
                      {SERVICE_LEVEL_PERMISSIONS.map((permission) => (
                        <label className='ua-service-permission' key={permission.key}>
                          <input
                            type='checkbox'
                            checked={escopo.permissions?.[permission.key] === true}
                            onChange={(event) => {
                              const enabled = event.target.checked
                              setEscopo((current) => ({
                                ...current,
                                permissions: {
                                  ...NO_SERVICE_LEVEL_PERMISSIONS,
                                  ...current.permissions,
                                  [permission.key]: enabled,
                                },
                              }))
                            }}
                          />
                          <span>
                            <strong>{permission.label}</strong>
                            <small>{permission.description}</small>
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div className='ua-scope-card'>
                    <div className='ua-scope-card__title'>
                      <span>
                        <Building2 size={17} /> Escopo herdado da empresa
                      </span>
                      <span className='ua-badge'>Somente leitura</span>
                    </div>
                    {escopo.empresa ? (
                      <>
                        <p>
                          <strong>{escopo.empresa.company_name}</strong> ·{' '}
                          {escopo.empresa.tipo_nome}
                          <br />
                          Este vínculo vem do cadastro da empresa e não pode ser alterado nesta
                          tela.
                        </p>
                        {escopo.empresa.itens.length ? (
                          <div className='ua-scope-tags'>
                            {escopo.empresa.itens.map((item) => (
                              <span key={item.id}>{item.nome}</span>
                            ))}
                          </div>
                        ) : (
                          <div className='ua-feedback ua-feedback--danger'>
                            {escopo.empresa.sem_correspondencia
                              ? `Os IDs externos (${escopo.empresa.ids_externos.join(
                                  ', '
                                )}) não correspondem a dados do Nível de Serviço.`
                              : 'A empresa não possui ID externo (BI) configurado.'}
                          </div>
                        )}
                      </>
                    ) : (
                      <p>Sem empresa vinculada.</p>
                    )}
                  </div>

                  <div className='ua-service-form__grid'>
                    <div className='ua-form-field'>
                      <label htmlFor='service-bu-select'>Unidades de negócio (BU)</label>
                      <Select
                        inputId='service-bu-select'
                        isMulti
                        isDisabled={!temAcesso || salvando}
                        options={opcoesBus}
                        value={valorMulti(escopo.bus, opcoesBus)}
                        onChange={(value: any) =>
                          setEscopo((current) => ({
                            ...current,
                            bus: value.length ? value.map((option: Opcao) => option.value) : null,
                          }))
                        }
                        placeholder='Todas as BUs'
                        noOptionsMessage={() => 'Nenhuma BU encontrada'}
                        classNamePrefix='admin-select'
                      />
                      <small>
                        Sem seleção, a pessoa acessa todas as BUs permitidas pela empresa.
                      </small>
                    </div>

                    <div className='ua-form-field'>
                      <label htmlFor='service-seller-select'>Carteira de sellers</label>
                      <Select
                        inputId='service-seller-select'
                        isMulti
                        isDisabled={!temAcesso || salvando}
                        options={opcoesSellers}
                        value={valorMulti(escopo.sellers, opcoesSellers)}
                        onChange={(value: any) =>
                          setEscopo((current) => ({
                            ...current,
                            sellers: value.length
                              ? value.map((option: Opcao) => option.value)
                              : null,
                          }))
                        }
                        placeholder='Todos os sellers'
                        noOptionsMessage={() => 'Nenhum seller encontrado'}
                        classNamePrefix='admin-select'
                      />
                      <small>Sem seleção, a pessoa acessa toda a carteira permitida.</small>
                    </div>
                  </div>

                  <div className='ua-detail-level'>
                    <div className='ua-detail-level__heading'>
                      <span>
                        <Layers3 size={17} /> Nível de detalhe
                      </span>
                      <small>Escolha a profundidade máxima das informações.</small>
                    </div>
                    <div className='ua-detail-level__options'>
                      {NIVEIS.map((nivel) => (
                        <label
                          className={`ua-detail-option ${
                            escopo.detail_level === nivel.value ? 'is-active' : ''
                          }`}
                          key={nivel.value}
                        >
                          <input
                            type='radio'
                            name='service-detail-level'
                            checked={escopo.detail_level === nivel.value}
                            disabled={!temAcesso || salvando}
                            onChange={() =>
                              setEscopo((current) => ({
                                ...current,
                                detail_level: nivel.value as 1 | 2 | 3,
                              }))
                            }
                          />
                          <span className='ua-detail-option__number'>{nivel.value}</span>
                          <span>
                            <strong>{nivel.label}</strong>
                            <small>{nivel.desc}</small>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className='ua-service-config__footer'>
                <span>As alterações de permissões e escopo entram em vigor após salvar.</span>
                <button
                  type='button'
                  className='ua-primary-button'
                  data-analytics-feature='Nível de Serviço · Salvar permissões e escopo'
                  onClick={salvarEscopo}
                  disabled={salvando || !temAcesso}
                >
                  {salvando ? <span className='ua-spinner' /> : <Save size={15} />}
                  {salvando ? 'Salvando…' : 'Salvar permissões e escopo'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
